import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {EAutocompleteOption} from 'webapp-lib/Components';
import {useDebouncedValue, useMountEffect} from 'webapp-lib/utils';

import userHandledBackend, {UserInResponse} from '@lexian/api/user-api';
import {Filter} from '_/utilities';
import {EFAutocomplete, EFAutocompleteProps, useWatch} from '_/utilities/form';
import {
  handlePaginatedLoadableFetch,
  PaginatedItemLoader,
} from '_/utilities/simple-fetch';
import {
  defaultPaginationArguments,
  PageItem,
  PaginatedLoadable,
  PaginationArguments,
  PaginationDirection,
  PaginationMode,
  State,
} from '_/utilities/types';

import {ActiveState} from '../ActiveFilter/ActiveFilter.types';
import {EFTagsAutocomplete} from '../ETagsAutocomplete';
import {OptionFilterInfo} from '../OptionFilterInfo/OptionFilterInfo';

import {useUserSearchFilters} from './UserAutocomplete.hooks';

const buildSearchUsers =
  (excludeCurrentUser?: boolean) =>
  (
    paginationArguments: Omit<PaginationArguments, 'cursor'>,
    currentLoadable: PaginatedLoadable<PageItem<UserInResponse>[]>,
    {searchQuery, isActive}: {searchQuery: string; isActive?: boolean}
  ): Promise<PaginatedLoadable<PageItem<UserInResponse>[]>> =>
    handlePaginatedLoadableFetch(
      {
        endpointFunction: userHandledBackend.searchUsers,
        otherParams: {
          query: searchQuery,
          isActive,
          excludeCurrentUser: Boolean(excludeCurrentUser),
        },
        getPageItemFromResponse: (body) => body.users,
      },
      paginationArguments,
      currentLoadable
    );

const filterToFlag = (activeStateFilter: Filter<ActiveState, true>) =>
  activeStateFilter?.item === 'ACTIVE'
    ? true
    : activeStateFilter?.item === 'INACTIVE'
      ? false
      : undefined;

const UUserAutocomplete = ({
  onUserChange,
  excludeCurrentUser,
  alreadySelectedUserIds,
  searchFiltersPersistenceKey = 'user-autocomplete',
  onSelectedUserIsFilteredOut,
  multiple = false,
  ...otherProps
}: Omit<EFAutocompleteProps, 'options'> & {
  onUserChange?: (user: UserInResponse | undefined) => void;
  excludeCurrentUser?: boolean;
  alreadySelectedUserIds?: string[];
  searchFiltersPersistenceKey?: string;
  onSelectedUserIsFilteredOut?: () => void;
  multiple?: boolean;
}): React.ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedUserOnMountInDataChecked = useRef(false);
  const [debounceSearchQuery, searchQuery, setSearchQuery] =
    useDebouncedValue('');
  const [userListStore] = useState(
    new PaginatedItemLoader(buildSearchUsers(excludeCurrentUser))
  );

  const selectedUserId = useWatch({
    control: otherProps.control,
    name: otherProps.name,
    defaultValue: otherProps.defaultValue,
  });

  function checkIfSelectedUserIsInData() {
    if (!onSelectedUserIsFilteredOut || !selectedUserId) {
      return;
    }
    const isSelectedUserInData = userListStore.loadable.data?.some(
      ({item}) => item.id === selectedUserId
    );
    if (!isSelectedUserInData) {
      onSelectedUserIsFilteredOut();
      inputRef.current?.focus();
    }
    setShowFilteredOutWarning(!isSelectedUserInData);
  }

  const {
    persistenceInitialized,
    initialActiveStateFilter,
    searchFilterText,
    searchFiltersDialog,
    showSearchFiltersDialog,
  } = useUserSearchFilters({
    persistenceKey: searchFiltersPersistenceKey,
    onFilterChange: (updatedActiveStateFilter) => {
      setIsActive(filterToFlag(updatedActiveStateFilter));
      checkIfSelectedUserIsInData();
    },
  });
  const [showFilteredOutWarning, setShowFilteredOutWarning] = useState(false);

  useEffect(() => {
    if (
      !onSelectedUserIsFilteredOut ||
      !persistenceInitialized ||
      userListStore.loadable.state === State.Loading ||
      selectedUserOnMountInDataChecked.current
    ) {
      return;
    }

    checkIfSelectedUserIsInData();
    selectedUserOnMountInDataChecked.current = true;
    // should only trigger on data change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userListStore.loadable.data]);

  const userOptions = useMemo(
    (): EAutocompleteOption[] =>
      (userListStore.loadable.data ?? []).map(
        (pageItemUser: PageItem<UserInResponse>) => ({
          displayText: pageItemUser.item.displayName,
          value: pageItemUser.item.id,
          disabled: (alreadySelectedUserIds ?? []).includes(
            pageItemUser.item.id
          ),
        })
      ),
    [alreadySelectedUserIds, userListStore.loadable.data]
  );

  const [isActive, setIsActive] = useState<boolean>();
  useMountEffect(() => {
    setIsActive(filterToFlag(initialActiveStateFilter));
  });

  const onLoadMoreUsersBefore = useCallback(async () => {
    await userListStore.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Backward},
      {searchQuery, isActive}
    );
  }, [userListStore, searchQuery, isActive]);

  const onLoadMoreUsersAfter = useCallback(async () => {
    await userListStore.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Forward},
      {searchQuery, isActive}
    );
  }, [userListStore, searchQuery, isActive]);

  const onChangeHandler = useCallback(
    (
      e: React.ChangeEvent<any>,
      originalEventHandler: React.ChangeEventHandler
    ) => {
      if (onUserChange) {
        const selectedUser = (userListStore.loadable.data || []).find(
          (user) => user.item.id === e.target.value
        );
        onUserChange(selectedUser?.item);
      }
      originalEventHandler(e);
    },
    [onUserChange, userListStore.loadable.data]
  );

  // handle fetching logic
  useEffect(() => {
    if (selectedUserId && !debounceSearchQuery && !multiple) {
      userListStore.fetch(
        {
          mode: PaginationMode.Navigate,
          key: [selectedUserId],
        },
        {searchQuery: '', isActive}
      );
    } else {
      userListStore.fetch(defaultPaginationArguments, {
        searchQuery: debounceSearchQuery,
        isActive,
      });
    }
  }, [debounceSearchQuery, isActive, selectedUserId, userListStore, multiple]);

  const optionFilterInfo = (
    <OptionFilterInfo
      optionFilterText={searchFilterText}
      showFilteredOutWarning={showFilteredOutWarning}
    />
  );

  return (
    <>
      {multiple ? (
        <EFTagsAutocomplete
          {...otherProps}
          customOnChange={onChangeHandler}
          hasMoreAfter={userListStore.loadable.hasMoreAfter}
          hasMoreBefore={userListStore.loadable.hasMoreBefore}
          optionFilterInfo={optionFilterInfo}
          options={userOptions}
          noOptionFiltering
          onFilterClick={showSearchFiltersDialog}
          onLoadMoreAfter={onLoadMoreUsersAfter}
          onLoadMoreBefore={onLoadMoreUsersBefore}
          onSearchQueryChange={setSearchQuery}
        />
      ) : (
        <EFAutocomplete
          {...otherProps}
          customOnChange={onChangeHandler}
          hasMoreAfter={userListStore.loadable.hasMoreAfter}
          hasMoreBefore={userListStore.loadable.hasMoreBefore}
          optionFilterInfo={optionFilterInfo}
          options={userOptions}
          noOptionFiltering
          onFilterClick={showSearchFiltersDialog}
          onLoadMoreAfter={onLoadMoreUsersAfter}
          onLoadMoreBefore={onLoadMoreUsersBefore}
          onSearchQueryChange={setSearchQuery}
        />
      )}
      {searchFiltersDialog}
    </>
  );
};
export const UserAutocomplete = observer(UUserAutocomplete);
